import React from 'react';
import { createPortal } from 'react-dom';
import { FONT_FAMILIES } from '../assets/fonts';
import {
  FONT_SIZE_OPTIONS, lineSpacingOptions, toolbarBtn, separatorStyle, popularColors,
} from '../hooks/useToolbarState';
import {
  IconBold, IconItalic, IconUnderline, IconStrikethrough, IconList, IconListNumbers,
  IconH1, IconH2, IconAlignLeft, IconAlignCenter, IconAlignRight, IconAlignJustified,
  IconHighlight, IconLineHeight,
} from '@tabler/icons-react';
import {
  applyBold, applyItalic, applyUnderline, applyStrikethrough, applyTextAlign,
  applyFontSize, applyFontFamily, applyColor, applyHighlight,
  applyBulletList, applyOrderedList,
  applyImageTextBold, applyImageTextItalic, applyImageTextUnderline, applyImageTextStrikethrough,
  applyImageTextColor,
  getSelectedTextFontSize,
} from '../utils/lessonFormattingUtils';

const activeStyle = {
  background: 'linear-gradient(135deg, #ee5a24 0%, #ff6b35 100%)',
  color: '#ffffff',
  boxShadow: '0 4px 12px rgba(238, 90, 36, 0.25)',
};
const btnStyle = (active) => ({ ...toolbarBtn.base, ...(active ? activeStyle : {}) });
const h1BtnStyle = btnStyle;
const h2BtnStyle = btnStyle;
const boldBtnStyle = btnStyle;
const italicBtnStyle = btnStyle;
const underlineBtnStyle = btnStyle;
const strikethroughBtnStyle = btnStyle;
const bulletListBtnStyle = btnStyle;
const orderedListBtnStyle = btnStyle;
const alignmentBtnStyle = btnStyle;
const lineSpacingBtnStyle = btnStyle;
const colorBtnStyle = btnStyle;
const highlightBtnStyle = btnStyle;

export default function LessonToolbar({
  fontFamilyRef, fontSizeRef,
  fontSelectHovered, setFontSelectHovered,
  fontSizeHovered, setFontSizeHovered,
  fontSizeFocused, setFontSizeFocused,
  fontFamilyDropdownOpen, setFontFamilyDropdownOpen,
  fontSizeDropdownOpen, setFontSizeDropdownOpen,
  fontFamilyDropdownPos, setFontFamilyDropdownPos,
  fontSizeDropdownPos, setFontSizeDropdownPos,
  showColorPopup, setShowColorPopup,
  colorButtonRef, setColorButtonRef,
  showHighlightPopup, setShowHighlightPopup,
  highlightButtonRef, setHighlightButtonRef,
  currentAlignment, setCurrentAlignment,
  currentLineSpacing, setCurrentLineSpacing,
  activeEditor,
  getActiveElement,
}) {
  return (
    <>
      {/* Global Toolbar */}
      <div 
        className="toolbar" 
        data-toolbar-element="true"
        onMouseEnter={() => {
          // При наведении на панель, сохраняем фокус на текстовом поле, если оно редактируется
          const textInput = document.querySelector('.image-text-input');
          if (textInput && document.activeElement !== textInput) {
            // Проверяем, что текстовое поле все еще существует и редактируется
            setTimeout(() => {
              if (textInput && window.selectedImageTextElement) {
                textInput.focus();
              }
            }, 0);
          }
        }}
        onMouseLeave={() => {
          // При уходе с панели, если текстовое поле не в фокусе, не делаем ничего
          // onBlur сам обработает ситуацию
        }}
      >
        {/* Font family select */}
        <span
          ref={fontFamilyRef}
          style={{
            marginRight: 8,
            fontSize: 15,
            height: 36,
            borderRadius: 12,
            border: `1px solid ${fontSelectHovered ? '#ee5a24' : '#e5e7eb'}`,
            background: '#fff',
            padding: '0 16px',
            boxSizing: 'border-box',
            outline: 'none',
            appearance: 'none',
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            verticalAlign: 'middle',
            cursor: 'pointer',
            userSelect: 'none',
            fontFamily: (() => {
              const activeElement = getActiveElement();
              if (activeElement && activeElement.chain) {
                return activeElement.getAttributes('textStyle')?.fontFamily || 'Arial';
              } else if (activeElement && activeElement.fontFamily) {
                // Текстовый элемент на изображении
                return activeElement.fontFamily || 'Arial';
              } else if (activeElement) {
                return activeElement.style.fontFamily || 'Arial';
              }
              return 'Arial';
            })(),
            color: fontSelectHovered ? '#ee5a24' : '#6b7280',
            transition: 'border 0.15s, color 0.15s',
            position: 'relative',
            minWidth: 120,
            maxWidth: 120,
            justifyContent: 'space-between',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            const rect = fontFamilyRef.current.getBoundingClientRect();
            setFontFamilyDropdownPos({ top: rect.bottom + 12, left: rect.left + rect.width / 2 });
            setFontFamilyDropdownOpen(v => !v);
            // Возвращаем фокус на текстовое поле, если оно редактируется
            setTimeout(() => {
              const textInput = document.querySelector('.image-text-input');
              if (textInput && window.selectedImageTextElement) {
                textInput.focus();
              }
            }, 0);
          }}
          onMouseDown={e => {
            e.preventDefault();
            // Возвращаем фокус на текстовое поле, если оно редактируется
            setTimeout(() => {
              const textInput = document.querySelector('.image-text-input');
              if (textInput && window.selectedImageTextElement) {
                textInput.focus();
              }
            }, 0);
          }}
          onMouseEnter={() => setFontSelectHovered(true)}
          onMouseLeave={() => setFontSelectHovered(false)}
        >
          <span style={{ 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap',
            flex: 1,
            textAlign: 'left'
          }}>
            {(() => {
              const activeElement = getActiveElement();
              if (activeElement && activeElement.chain) {
                return activeElement.getAttributes('textStyle')?.fontFamily || 'Arial';
              } else if (activeElement && activeElement.fontFamily) {
                // Текстовый элемент на изображении
                return activeElement.fontFamily || 'Arial';
              } else if (activeElement) {
                return activeElement.style.fontFamily || 'Arial';
              }
              return 'Arial';
            })()}
          </span>
          <span style={{ marginLeft: 8, fontSize: 12, color: '#bbb', flexShrink: 0 }}>▼</span>
        </span>
        {fontFamilyDropdownOpen && createPortal(
          <div
            style={{
              position: 'fixed',
              top: fontFamilyDropdownPos.top,
              left: fontFamilyDropdownPos.left,
              transform: 'translateX(-50%)',
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 10,
              boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
              zIndex: 1002,
              minWidth: 180,
              maxHeight: 320,
              overflowY: 'auto',
              padding: 4,
            }}
            onClick={e => e.stopPropagation()}
            onWheel={e => {
              e.stopPropagation();
              e.preventDefault();
              // Позволяем прокрутку внутри списка
              const target = e.currentTarget;
              const scrollTop = target.scrollTop;
              const scrollHeight = target.scrollHeight;
              const clientHeight = target.clientHeight;
              
              if (e.deltaY > 0 && scrollTop < scrollHeight - clientHeight) {
                // Прокрутка вниз
                target.scrollTop += 20;
              } else if (e.deltaY < 0 && scrollTop > 0) {
                // Прокрутка вверх
                target.scrollTop -= 20;
              }
            }}
          >
            {FONT_FAMILIES.map(f => (
              <div
                key={f}
                style={{
                  fontFamily: f,
                  fontSize: 16,
                  padding: '7px 12px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  background: (() => {
                    const activeElement = getActiveElement();
                    if (activeElement && activeElement.chain) {
                      return (activeElement.getAttributes('textStyle')?.fontFamily || 'Arial') === f ? '#fff7f0' : 'none';
                    } else if (activeElement && activeElement.fontFamily) {
                      // Текстовый элемент на изображении
                      return (activeElement.fontFamily || 'Arial') === f ? '#fff7f0' : 'none';
                    } else if (activeElement) {
                      return (activeElement.style.fontFamily || 'Arial') === f ? '#fff7f0' : 'none';
                    }
                    return 'Arial' === f ? '#fff7f0' : 'none';
                  })(),
                  color: (() => {
                    const activeElement = getActiveElement();
                    if (activeElement && activeElement.chain) {
                      return (activeElement.getAttributes('textStyle')?.fontFamily || 'Arial') === f ? '#ee5a24' : '#6b7280';
                    } else if (activeElement && activeElement.fontFamily) {
                      // Текстовый элемент на изображении
                      return (activeElement.fontFamily || 'Arial') === f ? '#ee5a24' : '#6b7280';
                    } else if (activeElement) {
                      return (activeElement.style.fontFamily || 'Arial') === f ? '#ee5a24' : '#6b7280';
                    }
                    return 'Arial' === f ? '#ee5a24' : '#6b7280';
                  })(),
                  marginBottom: 2,
                  transition: 'background 0.12s, color 0.12s',
                  fontWeight: 500,
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.color = '#ee5a24';
                }}
                  onMouseLeave={e => {
                  const activeElement = getActiveElement();
                  let currentFont = 'Arial';
                  if (activeElement && activeElement.chain) {
                    currentFont = activeElement.getAttributes('textStyle')?.fontFamily || 'Arial';
                  } else if (activeElement && activeElement.fontFamily) {
                    // Текстовый элемент на изображении
                    currentFont = activeElement.fontFamily || 'Arial';
                  } else if (activeElement) {
                    currentFont = activeElement.style.fontFamily || 'Arial';
                  }
                  e.currentTarget.style.background = currentFont === f ? '#fff7f0' : 'none';
                  e.currentTarget.style.color = currentFont === f ? '#ee5a24' : '#6b7280';
                }}
                onMouseDown={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  const activeElement = getActiveElement();
                  if (activeElement && activeElement.chain) {
                    // Это ProseMirror редактор
                    activeElement.chain().focus().setFontFamily(f).run();
                  } else if (activeElement && activeElement.fontFamily) {
                    // Это текстовый элемент на изображении
                    applyFontFamily(f);
                  } else if (activeElement) {
                    // Это текстовый элемент на доске
                    applyFontFamily(f);
                  }
                  setFontFamilyDropdownOpen(false);
                  // Возвращаем фокус на текстовое поле, если оно редактируется
                  setTimeout(() => {
                    const textInput = document.querySelector('.image-text-input');
                    if (textInput && window.selectedImageTextElement) {
                      textInput.focus();
                    }
                  }, 0);
                }}
              >
                {f}
              </div>
            ))}
          </div>,
          document.body
        )}
        {/* Font size custom control */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            marginRight: 8,
            minWidth: 80,
            height: 34,
            borderRadius: 12,
            background: '#fff',
            border: `1px solid ${fontSizeFocused ? '#ee5a24' : '#e5e7eb'}`,
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            justifyContent: 'center',
            padding: 0,
            transition: 'border 0.15s',
          }}
          tabIndex={0}
          onFocus={() => setFontSizeFocused(true)}
          onBlur={() => setFontSizeFocused(false)}
        >
          <button
            className="font-size-btn"
            style={{
              border: 'none',
              background: 'none',
              fontSize: 20,
              color: '#6b7280',
              width: 32,
              height: 32,
              borderRadius: 12,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              outline: 'none',
              margin: 0,
              padding: 0,
              boxShadow: 'none',
              transition: 'color 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#ee5a24'}
            onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}
            onFocus={e => e.currentTarget.style.color = '#ee5a24'}
            onBlur={e => e.currentTarget.style.color = '#6b7280'}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const activeElement = getActiveElement();
              if (activeElement && activeElement.chain) {
                // Это ProseMirror редактор
                const sizes = FONT_SIZE_OPTIONS;
                const current = Number((activeElement.getAttributes('textStyle')?.fontSize || '16px').replace('px',''));
                const idx = sizes.indexOf(current);
                if (idx > 0) {
                  activeElement.chain().focus().setFontSize(sizes[idx - 1] + 'px').run();
                }
              } else if (activeElement && activeElement.fontSize) {
                // Текстовый элемент на изображении
                const sizes = FONT_SIZE_OPTIONS;
                const current = parseInt(activeElement.fontSize);
                const idx = sizes.indexOf(current);
                if (idx > 0) {
                  applyFontSize(sizes[idx - 1]);
                }
              } else if (activeElement) {
                // Это текстовый элемент на доске
                const selectedFontSize = getSelectedTextFontSize();
                let currentSize;
                if (selectedFontSize) {
                  // Используем размер выделенного текста
                  currentSize = parseInt(selectedFontSize.replace('px', ''));
                } else {
                  // Используем размер элемента
                  currentSize = parseInt(activeElement.style.fontSize || '16');
                }
                const sizes = FONT_SIZE_OPTIONS;
                const idx = sizes.indexOf(currentSize);
                if (idx > 0) {
                  applyFontSize(sizes[idx - 1]);
                }
              }
              // Возвращаем фокус на текстовое поле, если оно редактируется
              setTimeout(() => {
                const textInput = document.querySelector('.image-text-input');
                if (textInput && window.selectedImageTextElement) {
                  textInput.focus();
                }
              }, 0);
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Возвращаем фокус на текстовое поле, если оно редактируется
              setTimeout(() => {
                const textInput = document.querySelector('.image-text-input');
                if (textInput && window.selectedImageTextElement) {
                  textInput.focus();
                }
              }, 0);
            }}
            tabIndex={-1}
            title="Меньше"
          >–</button>
          <span
            ref={fontSizeRef}
            style={{
              minWidth: 28,
              textAlign: 'center',
              fontSize: 15,
              fontWeight: 400,
              color: fontSizeHovered ? '#ee5a24' : '#6b7280',
              letterSpacing: 1,
              userSelect: 'none',
              margin: '0 1px',
              display: 'inline-block',
              cursor: 'pointer',
              borderRadius: 8,
              transition: 'background 0.15s, color 0.15s',
              position: 'relative',
            }}
            onMouseEnter={() => setFontSizeHovered(true)}
            onMouseLeave={() => setFontSizeHovered(false)}
            onClick={e => {
              const rect = fontSizeRef.current.getBoundingClientRect();
              setFontSizeDropdownPos({ top: rect.bottom + 21, left: rect.left + rect.width / 2 });
              setFontSizeDropdownOpen(v => !v);
            }}
                      >
              {(() => {
                const activeElement = getActiveElement();
                if (activeElement && activeElement.chain) {
                  // Это ProseMirror редактор
                  const fontSize = (activeElement.getAttributes('textStyle')?.fontSize || '16px').replace('px', '');
                  return parseFloat(fontSize).toFixed(1).replace('.', ',');
                } else if (activeElement && activeElement.fontSize) {
                  // Текстовый элемент на изображении
                  return parseFloat(activeElement.fontSize).toFixed(1).replace('.', ',');
                } else if (activeElement) {
                  // Это текстовый элемент на доске
                  // Сначала проверяем размер шрифта выделенного текста
                  const selectedFontSize = getSelectedTextFontSize();
                  if (selectedFontSize) {
                    return parseFloat(selectedFontSize.replace('px', '')).toFixed(1).replace('.', ',');
                  }
                  // Если нет выделения, используем размер элемента
                  const fontSize = (activeElement.style.fontSize || '16px').replace('px', '');
                  return parseFloat(fontSize).toFixed(1).replace('.', ',');
                }
                return '16';
              })()}
            </span>
          {fontSizeDropdownOpen && createPortal(
            <div
              style={{
                position: 'fixed',
                top: fontSizeDropdownPos.top,
                left: fontSizeDropdownPos.left,
                transform: 'translateX(-50%)',
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
                zIndex: 1002,
                minWidth: 60,
                maxHeight: 220,
                overflowY: 'auto',
                padding: 4,
              }}
              onClick={e => e.stopPropagation()}
              onWheel={e => {
                e.stopPropagation();
                e.preventDefault();
                // Позволяем прокрутку внутри списка
                const target = e.currentTarget;
                const scrollTop = target.scrollTop;
                const scrollHeight = target.scrollHeight;
                const clientHeight = target.clientHeight;
                
                if (e.deltaY > 0 && scrollTop < scrollHeight - clientHeight) {
                  // Прокрутка вниз
                  target.scrollTop += 20;
                } else if (e.deltaY < 0 && scrollTop > 0) {
                  // Прокрутка вверх
                  target.scrollTop -= 20;
                }
              }}
            >
              {FONT_SIZE_OPTIONS.map(size => (
                <div
                  key={size}
                  style={{
                    padding: '4px 12px',
                    fontSize: 15,
                    color: (() => {
                      const activeElement = getActiveElement();
                      if (activeElement && activeElement.chain) {
                        return (activeElement.getAttributes('textStyle')?.fontSize || '16px').replace('px','') === size ? '#ee5a24' : '#6b7280';
                      } else if (activeElement && activeElement.fontSize) {
                        // Текстовый элемент на изображении
                        return parseInt(activeElement.fontSize) === size ? '#ee5a24' : '#6b7280';
                      } else if (activeElement) {
                        return (activeElement.style.fontSize || '16px').replace('px','') === size ? '#ee5a24' : '#6b7280';
                      }
                      return '16' === size ? '#ee5a24' : '#6b7280';
                    })(),
                    background: (() => {
                      const activeElement = getActiveElement();
                      if (activeElement && activeElement.chain) {
                        return (activeElement.getAttributes('textStyle')?.fontSize || '16px').replace('px','') === size ? '#fff7f0' : 'none';
                      } else if (activeElement && activeElement.fontSize) {
                        // Текстовый элемент на изображении
                        return parseInt(activeElement.fontSize) === size ? '#fff7f0' : 'none';
                      } else if (activeElement) {
                        return (activeElement.style.fontSize || '16px').replace('px','') === size ? '#fff7f0' : 'none';
                      }
                      return '16' === size ? '#fff7f0' : 'none';
                    })(),
                    borderRadius: 6,
                    cursor: 'pointer',
                    marginBottom: 2,
                    textAlign: 'center',
                    fontWeight: 400,
                    transition: 'background 0.12s, color 0.12s',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = '#f3f4f6';
                    e.currentTarget.style.color = '#ee5a24';
                  }}
                  onMouseLeave={e => {
                    const activeElement = getActiveElement();
                    let currentSize = '16';
                    if (activeElement && activeElement.chain) {
                      currentSize = (activeElement.getAttributes('textStyle')?.fontSize || '16px').replace('px','');
                    } else if (activeElement && activeElement.fontSize) {
                      // Текстовый элемент на изображении
                      currentSize = String(parseInt(activeElement.fontSize));
                    } else if (activeElement) {
                      currentSize = (activeElement.style.fontSize || '16px').replace('px','');
                    }
                    e.currentTarget.style.background = currentSize === size ? '#fff7f0' : 'none';
                    e.currentTarget.style.color = currentSize === size ? '#ee5a24' : '#6b7280';
                  }}
                  onMouseDown={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    const activeElement = getActiveElement();
                    if (activeElement && activeElement.chain) {
                      // Это ProseMirror редактор
                      activeElement.chain().focus().setFontSize(size + 'px').run();
                    } else if (activeElement && activeElement.fontSize) {
                      // Текстовый элемент на изображении
                      applyFontSize(size);
                    } else if (activeElement) {
                      // Это текстовый элемент на доске
                      applyFontSize(size);
                    }
                    setFontSizeDropdownOpen(false);
                    // Возвращаем фокус на текстовое поле, если оно редактируется
                    setTimeout(() => {
                      const textInput = document.querySelector('.image-text-input');
                      if (textInput && window.selectedImageTextElement) {
                        textInput.focus();
                      }
                    }, 0);
                  }}
                >
                  {size}
                </div>
              ))}
            </div>,
            document.body
          )}
          <button
            className="font-size-btn"
            style={{
              border: 'none',
              background: 'none',
              fontSize: 20,
              color: '#6b7280',
              width: 32,
              height: 32,
              borderRadius: 12,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              outline: 'none',
              margin: 0,
              padding: 0,
              boxShadow: 'none',
              transition: 'color 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#ee5a24'}
            onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}
            onFocus={e => e.currentTarget.style.color = '#ee5a24'}
            onBlur={e => e.currentTarget.style.color = '#6b7280'}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const activeElement = getActiveElement();
              if (activeElement && activeElement.chain) {
                // Это ProseMirror редактор
                const sizes = FONT_SIZE_OPTIONS;
                const current = Number((activeElement.getAttributes('textStyle')?.fontSize || '16px').replace('px',''));
                const idx = sizes.indexOf(current);
                if (idx < sizes.length - 1) {
                  activeElement.chain().focus().setFontSize(sizes[idx + 1] + 'px').run();
                }
              } else if (activeElement && activeElement.fontSize) {
                // Текстовый элемент на изображении
                const sizes = FONT_SIZE_OPTIONS;
                const current = parseInt(activeElement.fontSize);
                const idx = sizes.indexOf(current);
                if (idx < sizes.length - 1) {
                  applyFontSize(sizes[idx + 1]);
                }
              } else if (activeElement) {
                // Это текстовый элемент на доске
                const selectedFontSize = getSelectedTextFontSize();
                let currentSize;
                if (selectedFontSize) {
                  // Используем размер выделенного текста
                  currentSize = parseInt(selectedFontSize.replace('px', ''));
                } else {
                  // Используем размер элемента
                  currentSize = parseInt(activeElement.style.fontSize || '16');
                }
                const sizes = FONT_SIZE_OPTIONS;
                const idx = sizes.indexOf(currentSize);
                if (idx < sizes.length - 1) {
                  applyFontSize(sizes[idx + 1]);
                }
              }
              // Возвращаем фокус на текстовое поле, если оно редактируется
              setTimeout(() => {
                const textInput = document.querySelector('.image-text-input');
                if (textInput && window.selectedImageTextElement) {
                  textInput.focus();
                }
              }, 0);
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Возвращаем фокус на текстовое поле, если оно редактируется
              setTimeout(() => {
                const textInput = document.querySelector('.image-text-input');
                if (textInput && window.selectedImageTextElement) {
                  textInput.focus();
                }
              }, 0);
            }}
            tabIndex={-1}
            title="Больше"
          >+</button>
        </div>
        {/* Headings */}
        <button
          style={h1BtnStyle((() => {
            const activeElement = getActiveElement();
            if (activeElement && activeElement.chain) {
              return activeElement.isActive('heading', { level: 1 });
            }
            return false;
          })())}
          onClick={() => {
            const activeElement = getActiveElement();
            if (activeElement && activeElement.chain) {
              activeElement.chain().focus().toggleHeading({ level: 1 }).run();
            }
          }}
          title="Heading 1"
        >
          <IconH1 size={20} />
        </button>
        <button
          style={h2BtnStyle((() => {
            const activeElement = getActiveElement();
            if (activeElement && activeElement.chain) {
              return activeElement.isActive('heading', { level: 2 });
            }
            return false;
          })())}
          onClick={() => {
            const activeElement = getActiveElement();
            if (activeElement && activeElement.chain) {
              activeElement.chain().focus().toggleHeading({ level: 2 }).run();
            }
          }}
          title="Heading 2"
        >
          <IconH2 size={20} />
        </button>
        <span style={separatorStyle}></span>
        {/* Bold, Italic, Underline, Strike */}
        <button
          style={boldBtnStyle((() => {
            const activeElement = getActiveElement();
            if (activeElement && activeElement.chain) {
              return activeElement.isActive('bold');
            } else if (activeElement && activeElement.fontWeight !== undefined) {
              // Это текстовый элемент на изображении
              return activeElement.fontWeight === 'bold';
            } else if (activeElement) {
              if (activeElement && activeElement.style) {
                return activeElement.style.fontWeight === 'bold';
              }
              return false;
            }
            return false;
          })())}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const activeElement = getActiveElement();
            if (activeElement && activeElement.chain) {
              // Это ProseMirror редактор
              activeElement.chain().focus().toggleBold().run();
            } else if (activeElement && activeElement.fontWeight !== undefined) {
              // Это текстовый элемент на изображении
              applyImageTextBold(activeElement);
            } else if (activeElement) {
              // Это текстовый элемент на доске
              applyBold();
            }
            // Возвращаем фокус на текстовое поле, если оно редактируется
            setTimeout(() => {
              const textInput = document.querySelector('.image-text-input');
              if (textInput && window.selectedImageTextElement) {
                textInput.focus();
              }
            }, 0);
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Возвращаем фокус на текстовое поле, если оно редактируется
            setTimeout(() => {
              const textInput = document.querySelector('.image-text-input');
              if (textInput && window.selectedImageTextElement) {
                textInput.focus();
              }
            }, 0);
          }}
          title="Bold"
        >
          <IconBold size={20} />
        </button>
        <button
          style={italicBtnStyle((() => {
            const activeElement = getActiveElement();
            if (activeElement && activeElement.chain) {
              return activeElement.isActive('italic');
            } else if (activeElement && activeElement.fontStyle !== undefined) {
              // Это текстовый элемент на изображении
              return activeElement.fontStyle === 'italic';
            } else if (activeElement && activeElement.style) {
              return activeElement.style.fontStyle === 'italic';
            }
            return false;
          })())}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const activeElement = getActiveElement();
            if (activeElement && activeElement.chain) {
              // Это ProseMirror редактор
              activeElement.chain().focus().toggleItalic().run();
            } else if (activeElement && activeElement.fontStyle !== undefined) {
              // Это текстовый элемент на изображении
              applyImageTextItalic(activeElement);
            } else if (activeElement) {
              // Это текстовый элемент на доске
              applyItalic();
            }
            // Возвращаем фокус на текстовое поле, если оно редактируется
            setTimeout(() => {
              const textInput = document.querySelector('.image-text-input');
              if (textInput && window.selectedImageTextElement) {
                textInput.focus();
              }
            }, 0);
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Возвращаем фокус на текстовое поле, если оно редактируется
            setTimeout(() => {
              const textInput = document.querySelector('.image-text-input');
              if (textInput && window.selectedImageTextElement) {
                textInput.focus();
              }
            }, 0);
          }}
          title="Italic"
        >
          <IconItalic size={20} />
        </button>
        <button
          style={underlineBtnStyle((() => {
            const activeElement = getActiveElement();
            if (activeElement && activeElement.chain) {
              return activeElement.isActive('underline');
            } else if (activeElement && activeElement.textDecoration !== undefined) {
              // Это текстовый элемент на изображении
              return activeElement.textDecoration?.includes('underline');
            } else if (activeElement && activeElement.style) {
              return activeElement.style.textDecoration?.includes('underline');
            }
            return false;
          })())}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const activeElement = getActiveElement();
            if (activeElement && activeElement.chain) {
              // Это ProseMirror редактор
              activeElement.chain().focus().toggleUnderline().run();
            } else if (activeElement && activeElement.textDecoration !== undefined) {
              // Это текстовый элемент на изображении
              applyImageTextUnderline(activeElement);
            } else if (activeElement) {
              // Это текстовый элемент на доске
              applyUnderline();
            }
            // Возвращаем фокус на текстовое поле, если оно редактируется
            setTimeout(() => {
              const textInput = document.querySelector('.image-text-input');
              if (textInput && window.selectedImageTextElement) {
                textInput.focus();
              }
            }, 0);
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Возвращаем фокус на текстовое поле, если оно редактируется
            setTimeout(() => {
              const textInput = document.querySelector('.image-text-input');
              if (textInput && window.selectedImageTextElement) {
                textInput.focus();
              }
            }, 0);
          }}
          title="Underline"
        >
          <IconUnderline size={20} />
        </button>
        <button
          style={strikethroughBtnStyle((() => {
            const activeElement = getActiveElement();
            if (activeElement && activeElement.chain) {
              return activeElement.isActive('strike');
            } else if (activeElement && activeElement.textDecoration !== undefined) {
              // Это текстовый элемент на изображении
              return activeElement.textDecoration?.includes('line-through');
            } else if (activeElement && activeElement.style) {
              return activeElement.style.textDecoration?.includes('line-through');
            }
            return false;
          })())}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const activeElement = getActiveElement();
            if (activeElement && activeElement.chain) {
              // Это ProseMirror редактор
              activeElement.chain().focus().toggleStrike().run();
            } else if (activeElement && activeElement.textDecoration !== undefined) {
              // Это текстовый элемент на изображении
              applyImageTextStrikethrough(activeElement);
            } else if (activeElement) {
              // Это текстовый элемент на доске
              applyStrikethrough();
            }
            // Возвращаем фокус на текстовое поле, если оно редактируется
            setTimeout(() => {
              const textInput = document.querySelector('.image-text-input');
              if (textInput && window.selectedImageTextElement) {
                textInput.focus();
              }
            }, 0);
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Возвращаем фокус на текстовое поле, если оно редактируется
            setTimeout(() => {
              const textInput = document.querySelector('.image-text-input');
              if (textInput && window.selectedImageTextElement) {
                textInput.focus();
              }
            }, 0);
          }}
          title="Strikethrough"
        >
          <IconStrikethrough size={20} />
        </button>
        <span style={separatorStyle}></span>
        {/* Lists */}
        <button
          style={bulletListBtnStyle((() => {
            const activeElement = getActiveElement();
            if (activeElement && activeElement.chain) {
              return activeElement.isActive('bulletList');
            } else if (activeElement) {
              return activeElement.textContent && activeElement.textContent.startsWith('• ');
            }
            return false;
          })())}
          onClick={() => {
            const activeElement = getActiveElement();
            if (activeElement && activeElement.chain) {
              // Это ProseMirror редактор
              activeElement.chain().focus().toggleBulletList().run();
            } else if (activeElement) {
              // Это текстовый элемент на доске
              applyBulletList();
            }
          }}
          title="Bullet List"
        >
          <IconList size={20} />
        </button>
        <button
          style={orderedListBtnStyle((() => {
            const activeElement = getActiveElement();
            if (activeElement && activeElement.chain) {
              return activeElement.isActive('orderedList');
            } else if (activeElement) {
              return /^\d+\.\s/.test(activeElement.textContent);
            }
            return false;
          })())}
          onClick={() => {
            const activeElement = getActiveElement();
            if (activeElement && activeElement.chain) {
              // Это ProseMirror редактор
              activeElement.chain().focus().toggleOrderedList().run();
            } else if (activeElement) {
              // Это текстовый элемент на доске
              applyOrderedList();
            }
          }}
          title="Numbered List"
        >
          <IconListNumbers size={20} />
        </button>
        <span style={separatorStyle}></span>
        {/* Align */}
        <button
          style={alignmentBtnStyle((() => {
            const activeElement = getActiveElement();
            if (activeElement && activeElement.chain) {
              return activeElement.isActive({ textAlign: 'left' }) || 
                     activeElement.isActive({ textAlign: 'center' }) || 
                     activeElement.isActive({ textAlign: 'right' }) || 
                     activeElement.isActive({ textAlign: 'justify' });
            } else if (activeElement && activeElement.style) {
              const textAlign = activeElement.style.textAlign;
              return textAlign === 'left' || textAlign === 'center' || textAlign === 'right' || textAlign === 'justify';
            }
            return false;
          })())}
          onClick={() => {
            const activeElement = getActiveElement();
            const alignments = ['left', 'center', 'right', 'justify'];
            const currentIndex = alignments.indexOf(currentAlignment);
            const nextIndex = (currentIndex + 1) % alignments.length;
            const nextAlign = alignments[nextIndex];
            
            if (activeElement && activeElement.chain) {
              // Это ProseMirror редактор
              activeElement.chain().focus().setTextAlign(nextAlign).run();
            } else if (activeElement) {
              // Это текстовый элемент на доске
              applyTextAlign(nextAlign);
            }
            setCurrentAlignment(nextAlign);
          }}
          title={`Align ${currentAlignment.charAt(0).toUpperCase() + currentAlignment.slice(1)}`}
        >
          {(() => {
            switch (currentAlignment) {
              case 'center':
                return <IconAlignCenter size={20} />;
              case 'right':
                return <IconAlignRight size={20} />;
              case 'justify':
                return <IconAlignJustified size={20} />;
              default:
                return <IconAlignLeft size={20} />;
            }
          })()}
        </button>
        {/* Line Spacing */}
        <button
          style={lineSpacingBtnStyle(false)}
          onClick={() => {
            if (activeEditor) {
              const currentIndex = lineSpacingOptions.findIndex(option => option.value === currentLineSpacing);
              const nextIndex = (currentIndex + 1) % lineSpacingOptions.length;
              const nextSpacing = lineSpacingOptions[nextIndex];
              setCurrentLineSpacing(nextSpacing.value);
              if (activeEditor.view && activeEditor.view.dom) {
                activeEditor.view.dom.style.lineHeight = nextSpacing.value;
              }
            }
          }}
          title={`Line Spacing: ${currentLineSpacing}`}
        >
          <IconLineHeight size={20} />
        </button>
        <span style={separatorStyle}></span>
        {/* Color & Highlight */}
        <button
          ref={setColorButtonRef}
          style={{
            ...colorBtnStyle(false),
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2px',
            padding: '4px'
          }}
          onClick={(e) => {
            e.stopPropagation();
            setShowColorPopup(!showColorPopup);
          }}
          title="Text color"
        >
          <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#6b7280' }}>A</span>
          <div 
            style={{
              width: '12px',
              height: '6px',
              backgroundColor: (() => {
              const activeElement = getActiveElement();
              if (activeElement && activeElement.chain) {
                return activeElement.getAttributes('textStyle')?.color || '#22223b';
              } else if (activeElement && activeElement.color) {
                // Текстовый элемент на изображении
                return activeElement.color || '#22223b';
              } else if (activeElement) {
                return activeElement.style.color || '#22223b';
              }
              return '#22223b';
            })(),
              borderRadius: '1px',
              border: '1px solid #e0e0e0'
            }}
          />
        </button>
        <button
          ref={setHighlightButtonRef}
          style={highlightBtnStyle((() => {
            const activeElement = getActiveElement();
            if (activeElement && activeElement.chain) {
              return activeElement.isActive('highlight');
            } else if (activeElement && activeElement.style) {
              // Проверяем наличие подсветки в стилях
              return activeElement.style.backgroundColor && 
                     activeElement.style.backgroundColor !== 'transparent' && 
                     activeElement.style.backgroundColor !== 'rgba(0, 0, 0, 0)';
            }
            return false;
          })())}
          onClick={(e) => {
            e.stopPropagation();
            setShowHighlightPopup(!showHighlightPopup);
          }}
          title="Highlight"
        >
          <IconHighlight size={20} />
        </button>
      </div>
      {/* Color Popup */}
      {showColorPopup && colorButtonRef && (
        <div
          onClick={(e) => e.stopPropagation()}
          onWheel={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          style={{
            position: 'fixed',
            top: colorButtonRef.getBoundingClientRect().bottom + 13,
            left: colorButtonRef.getBoundingClientRect().left,
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            padding: '12px',
            zIndex: 1001,
            display: 'grid',
            gridTemplateColumns: 'repeat(8, 1fr)',
            gap: '4px',
            minWidth: '200px'
          }}
        >
          {popularColors.map((color, index) => (
            <button
              key={index}
              style={{
                width: '20px',
                height: '20px',
                backgroundColor: color,
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'transform 0.1s ease'
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const activeElement = getActiveElement();
                if (activeElement && activeElement.chain) {
                  // Работаем с ProseMirror редактором
                  const { from, to } = activeElement.state.selection;
                  activeElement.chain().setTextSelection({ from, to }).setColor(color).run();
                } else if (activeElement && activeElement.color !== undefined) {
                  // Текстовый элемент на изображении
                  applyImageTextColor(color, activeElement);
                } else if (activeElement) {
                  // Работаем с текстовыми элементами на доске
                  applyColor(color);
                }
                setShowColorPopup(false);
                // Возвращаем фокус на текстовое поле, если оно редактируется
                setTimeout(() => {
                  const textInput = document.querySelector('.image-text-input');
                  if (textInput && window.selectedImageTextElement) {
                    textInput.focus();
                  }
                }, 0);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Возвращаем фокус на текстовое поле, если оно редактируется
                setTimeout(() => {
                  const textInput = document.querySelector('.image-text-input');
                  if (textInput && window.selectedImageTextElement) {
                    textInput.focus();
                  }
                }, 0);
              }}
              title={color}
              onMouseEnter={e => e.target.style.transform = 'scale(1.1)'}
              onMouseLeave={e => e.target.style.transform = 'scale(1)'}
            />
          ))}
        </div>
      )}

      {/* Highlight Color Popup */}
      {showHighlightPopup && highlightButtonRef && (
        <div
          onClick={(e) => e.stopPropagation()}
          onWheel={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          style={{
            position: 'fixed',
            top: highlightButtonRef.getBoundingClientRect().bottom + 13,
            left: highlightButtonRef.getBoundingClientRect().left,
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            padding: '12px',
            zIndex: 1001,
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '4px',
            minWidth: '160px'
          }}
        >
          {[
            { class: 'pink', color: '#fce7f3', name: 'Pink' },
            { class: 'blue', color: '#dbeafe', name: 'Blue' },
            { class: 'green', color: '#dcfce7', name: 'Green' },
            { class: 'purple', color: '#f3e8ff', name: 'Purple' },
            { class: 'orange', color: '#fed7aa', name: 'Orange' },
            { class: 'yellow', color: '#fef3c7', name: 'Yellow' },
            { class: 'cyan', color: '#cffafe', name: 'Cyan' },
            { class: 'lavender', color: '#e9d5ff', name: 'Lavender' }
          ].map((highlightColor, index) => (
            <button
              key={index}
              style={{
                width: '24px',
                height: '24px',
                backgroundColor: highlightColor.color,
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'transform 0.1s ease'
              }}
              onClick={() => {
                const activeElement = getActiveElement();
                if (activeElement && activeElement.chain) {
                  // Работаем с ProseMirror редактором
                  const { from, to } = activeElement.state.selection;
                  if (from !== to) {
                    if (activeElement.isActive('highlight', { color: highlightColor.color })) {
                      activeElement.chain().focus().unsetHighlight().run();
                    } else {
                      activeElement.chain().focus().setHighlight({ color: highlightColor.color }).run();
                    }
                  }
                } else if (activeElement) {
                  // Работаем с текстовыми элементами на доске
                  applyHighlight(highlightColor.color);
                }
                setShowHighlightPopup(false);
              }}
              title={highlightColor.name}
              onMouseEnter={e => e.target.style.transform = 'scale(1.1)'}
              onMouseLeave={e => e.target.style.transform = 'scale(1)'}
            />
          ))}
        </div>
      )}

    </>
  );
}
